import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useMemo } from 'react';

// Data Layer
import {
  CoverageCatalog,
  Quotation,
  QuotationUnit,
} from 'data-layer-cr/dist/models';

// Design System
import { discountTypes } from 'data-layer-cr/dist/constants';
import { colors, DefaultCard, SubTitle1Text } from 'design-system';

// hooks
import { useCoverageColectiveResume } from 'src/components/screens/hooks';

// Components
import {
  QuotationCoverages,
  QuotationDeductibles,
  QuotationEndorsements,
  QuotationPriceHeader,
} from './components';

const useStyles = makeStyles(() =>
  createStyles({
    separatorLine: {
      borderBottom: `2px solid ${colors.gallery}`,
      marginRight: 12,
      marginLeft: 12,
    },
    logo: { marginRight: 11 },
  }),
);

type Props = {
  forceCollapsed?: boolean;
  onForceCollapsedUpdate?: () => void;
  isAutoPx: boolean;
  paymentQuantity: number;
  catalogCoverages: CoverageCatalog[];
  unit: QuotationUnit;
  isEditable: boolean;
  isDisplayedDeductibles?: boolean;
  isDisplayedEndorsements?: boolean;
  isDisplayedHeader?: boolean;
  quotation: Quotation;
};

const DynamicQuotationCard = ({
  forceCollapsed,
  onForceCollapsedUpdate,
  quotation,
  isAutoPx,
  paymentQuantity,
  unit,
  isEditable,
  catalogCoverages,
  isDisplayedDeductibles = true,
  isDisplayedEndorsements = true,
  isDisplayedHeader = true,
}: Props): JSX.Element => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();

  const { additionalBenefits, regularCoverageCatalogs } =
    useCoverageColectiveResume({
      quotationCoverages: catalogCoverages,
    });

  const { coverages, total } = unit;
  const { grandTotal } = total;

  const usedDiscount = total.discounts || [];

  const grandTotalAutoPx =
    usedDiscount.find(
      (discount) => discount.id === discountTypes.AUTOPX_DISCOUNT_ID,
    )?.total ?? 0;
  /**
   * The layout that will display the unit's totals
   */
  const totalsLayout = useMemo(() => {
    const usedPrice = isAutoPx ? grandTotalAutoPx : grandTotal;
    /** Price */
    return (
      <QuotationPriceHeader
        isAutoPx={isAutoPx}
        totalPrice={usedPrice}
        paymentQuantity={paymentQuantity}
        regularPrice={grandTotal}
        quotation={quotation}
      />
    );
  }, [paymentQuantity, grandTotal, grandTotalAutoPx, isAutoPx, quotation]);

  return (
    <DefaultCard
      title={
        <Grid item>
          <Box display="flex">
            <img
              src="svg/coverage_shield.svg"
              alt="coverage_shield_icon"
              className={classes.logo}
              data-testid="icon-image"
            />
            <SubTitle1Text>Coberturas</SubTitle1Text>
          </Box>
        </Grid>
      }
      nonCollapsibleElement={isDisplayedHeader ? totalsLayout : null}
      collapsible={isMobile}
      forceCollapsed={forceCollapsed}
      onForceCollapsedUpdate={onForceCollapsedUpdate}
    >
      <Grid container spacing={2}>
        {/** Coverages */}
        <Grid item xs={12} data-testid="quotation-coverages">
          <QuotationCoverages
            productCoverages={coverages}
            catalogCoverages={regularCoverageCatalogs}
            isEditable={isEditable}
            quotation={quotation}
          />
        </Grid>
        {isDisplayedDeductibles && (
          <>
            {/** Separator */}
            <Grid item container alignItems="center" xs={12}>
              <Grid
                item
                className={classes.separatorLine}
                xs={12}
                data-testid="deductibles-separator"
              />
            </Grid>

            {/** Deductibles */}
            <Grid item xs={12} data-testid="quotation-deductibles">
              <QuotationDeductibles
                productCoverages={coverages}
                catalogCoverages={regularCoverageCatalogs}
                quotation={quotation}
              />
            </Grid>
          </>
        )}

        {isDisplayedEndorsements && (
          <>
            {/** Separator */}
            <Grid item container alignItems="center" xs={12}>
              <Grid
                item
                className={classes.separatorLine}
                xs={12}
                data-testid="endorsements-separator"
              />
            </Grid>
            {/** Endorsements */}
            <Grid item xs={12} data-testid="quotation-endorsements">
              <QuotationEndorsements
                productEndorsements={coverages}
                catalogCoverages={regularCoverageCatalogs}
                isEditable={isEditable}
                quotation={quotation}
                additionalBenefits={additionalBenefits}
              />
            </Grid>
          </>
        )}
      </Grid>
    </DefaultCard>
  );
};

export default DynamicQuotationCard;
